/**
 * Point cloud property getters and registration — NON-HOOK VERSION FOR POTREE
 *
 * Framework-agnostic (no React, no Apollo). Importable from potree_fork and the client.
 *
 * Properties registered here:
 * - pointcloud_height_gradient (GRADIENT, PROJECT_USER)
 * - pointcloud_point_size (RANGE, PROJECT_USER)
 * - pointcloud_appearance (SELECT, PROJECT_USER)
 * - pointcloud_resolution (SELECT, PROJECT_USER) — uses `-1` sentinel for "no user choice"
 * - pointcloud_hidden_classifications (FLOAT_ARRAY, PROJECT_USER) — number[] of hidden class codes per pointcloud
 * - pointcloud_hidden_sources (FLOAT_ARRAY, PROJECT_USER) — number[] of hidden source ids per pointcloud
 * - viewer_edl_enabled (BOOLEAN, PROJECT_USER, viewer-scoped via objectIdentifier='viewer')
 *
 * Bounds-dependent properties (registered lazily via the helpers below, once a pointcloud's
 * `initialHeightMin/Max` and `initialIntensityMin/Max` are known):
 * - pointcloud_height_range (MIN_MAX, PROJECT_USER)
 * - pointcloud_intensity_range (MIN_MAX, PROJECT_USER)
 */

import { PropertyType, PropertyValueType, PropertyValueData } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';

// ConvertType.DISTANCE === 1 (kept as numeric literal to avoid pulling React into potree-shared)
const CONVERT_TYPE_DISTANCE = 1;

export const VIEWER_OBJECT_ID = 'viewer';

// ============================================================================
// PROPERTY IDENTIFIERS
// ============================================================================

export const PointCloudPropertyKeys = {
  HEIGHT_GRADIENT: 'pointcloud_height_gradient',
  POINT_SIZE: 'pointcloud_point_size',
  APPEARANCE: 'pointcloud_appearance',
  HEIGHT_RANGE: 'pointcloud_height_range',
  INTENSITY_RANGE: 'pointcloud_intensity_range',
  RESOLUTION: 'pointcloud_resolution',
  HIDDEN_CLASSIFICATIONS: 'pointcloud_hidden_classifications',
  HIDDEN_SOURCES: 'pointcloud_hidden_sources',
  VIEWER_EDL_ENABLED: 'viewer_edl_enabled',
} as const;

// ============================================================================
// REGISTRATION
// ============================================================================

export function registerPointCloudProperties() {
  if (!propertyRegistry.isRegistered(PointCloudPropertyKeys.HEIGHT_GRADIENT)) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: PointCloudPropertyKeys.HEIGHT_GRADIENT,
      name: 'Gradient',
      valueType: PropertyValueType.GRADIENT,
      defaultValue: 'SPECTRAL',
      description: 'Color gradient for point cloud colorization',
      ui: { tooltip: 'Select the color gradient used for point cloud colorization', useGradientStyling: true },
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered(PointCloudPropertyKeys.POINT_SIZE)) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: PointCloudPropertyKeys.POINT_SIZE,
      name: 'Point Size',
      valueType: PropertyValueType.RANGE,
      defaultValue: 1.0,
      validation: { min: 0, max: 2, step: 0.01 },
      ui: { tooltip: 'Adjust the size of individual points in the point cloud', group: 'Appearance' },
      description: 'Controls the visual size of points in the point cloud rendering',
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered(PointCloudPropertyKeys.APPEARANCE)) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: PointCloudPropertyKeys.APPEARANCE,
      name: 'Field',
      valueType: PropertyValueType.SELECT,
      defaultValue: 'rgba',
      validation: {
        options: [
          { value: 'rgba', label: 'RGB' },
          { value: 'classification', label: 'Classification' },
          { value: 'height', label: 'Height' },
          { value: 'intensity gradient', label: 'Intensity' },
          { value: 'source', label: 'Source' },
        ],
      },
      ui: { tooltip: 'Select the field used for point cloud colorization' },
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered(PointCloudPropertyKeys.RESOLUTION)) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: PointCloudPropertyKeys.RESOLUTION,
      name: 'Resolution',
      valueType: PropertyValueType.SELECT,
      defaultValue: -1,
      validation: { options: [{ value: 0, label: 'all points' }] },
      ui: { tooltip: 'Adjust the resolution level of the point cloud rendering', group: 'Filters', useUserPreferenceConvertType: CONVERT_TYPE_DISTANCE },
      description: 'Controls the level of detail in point cloud rendering',
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered(PointCloudPropertyKeys.HIDDEN_CLASSIFICATIONS)) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: PointCloudPropertyKeys.HIDDEN_CLASSIFICATIONS,
      name: 'Hidden Classifications',
      valueType: PropertyValueType.FLOAT_ARRAY,
      defaultValue: [],
      description: 'Class codes hidden for this point cloud (empty = all visible)',
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered(PointCloudPropertyKeys.HIDDEN_SOURCES)) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: PointCloudPropertyKeys.HIDDEN_SOURCES,
      name: 'Hidden Sources',
      valueType: PropertyValueType.FLOAT_ARRAY,
      defaultValue: [],
      description: 'Source ids hidden for this point cloud (empty = all visible)',
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered(PointCloudPropertyKeys.VIEWER_EDL_ENABLED)) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: PointCloudPropertyKeys.VIEWER_EDL_ENABLED,
      name: 'Point Edges',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: true,
      description: 'Toggles the display of edges around individual points (viewer-wide)',
      ui: { tooltip: 'Show or hide edges around individual points for better visibility', group: 'Appearance' },
      annotationTypes: [],
    });
  }
}

// Register at module import — mirrors registerPlaneProperties()
registerPointCloudProperties();

// ============================================================================
// LAZY REGISTRATION FOR BOUNDS-DEPENDENT PROPERTIES
// ============================================================================

/**
 * Register `pointcloud_height_range` once actual bounds are known.
 * Idempotent: subsequent calls are no-ops.
 */
export function registerHeightRangeIfMissing(initialMin: number, initialMax: number): void {
  if (propertyRegistry.isRegistered(PointCloudPropertyKeys.HEIGHT_RANGE)) return;
  propertyRegistry.register({
    type: PropertyType.PROJECT_USER,
    identifier: PointCloudPropertyKeys.HEIGHT_RANGE,
    name: 'Height Range',
    valueType: PropertyValueType.MIN_MAX,
    defaultValue: [initialMin, initialMax],
    description: 'Set the height range for point cloud filtering',
    validation: { min: initialMin, max: initialMax },
    ui: { tooltip: 'Set the height range for point cloud filtering', useUserPreferenceConvertType: CONVERT_TYPE_DISTANCE, showResetButton: true, decimals: 2 },
    annotationTypes: [],
  });
}

/**
 * Register `pointcloud_intensity_range` once actual bounds are known.
 * Idempotent: subsequent calls are no-ops.
 */
export function registerIntensityRangeIfMissing(initialMin: number, initialMax: number): void {
  if (propertyRegistry.isRegistered(PointCloudPropertyKeys.INTENSITY_RANGE)) return;
  propertyRegistry.register({
    type: PropertyType.PROJECT_USER,
    identifier: PointCloudPropertyKeys.INTENSITY_RANGE,
    name: 'Intensity Range',
    valueType: PropertyValueType.MIN_MAX,
    defaultValue: [initialMin, initialMax],
    description: 'Set the intensity range for point cloud filtering',
    validation: { min: initialMin, max: initialMax, step: 1 },
    ui: { tooltip: 'Set the intensity range for point cloud filtering', showResetButton: true, decimals: 0 },
    annotationTypes: [],
  });
}

// ============================================================================
// GETTERS
// ============================================================================

export function getPointCloudGradientProperty(pointCloudId: string): ReactiveProperty<string> {
  return getGlobalPropertyService().getProperty(PointCloudPropertyKeys.HEIGHT_GRADIENT, pointCloudId, PropertyType.PROJECT_USER) as ReactiveProperty<string>;
}

export function getPointCloudPointSizeProperty(pointCloudId: string): ReactiveProperty<number> {
  return getGlobalPropertyService().getProperty(PointCloudPropertyKeys.POINT_SIZE, pointCloudId, PropertyType.PROJECT_USER) as ReactiveProperty<number>;
}

export function getPointCloudAppearanceProperty(pointCloudId: string): ReactiveProperty<string> {
  return getGlobalPropertyService().getProperty(PointCloudPropertyKeys.APPEARANCE, pointCloudId, PropertyType.PROJECT_USER) as ReactiveProperty<string>;
}

export function getPointCloudResolutionProperty(pointCloudId: string): ReactiveProperty<number> {
  return getGlobalPropertyService().getProperty(PointCloudPropertyKeys.RESOLUTION, pointCloudId, PropertyType.PROJECT_USER) as ReactiveProperty<number>;
}

/**
 * Returns the height range property, registering it lazily if bounds are provided.
 * Pass `initialMin`/`initialMax` on first call; subsequent calls can omit them.
 */
export function getPointCloudHeightRangeProperty(pointCloudId: string, initialMin?: number, initialMax?: number): ReactiveProperty<[number, number]> {
  if (initialMin !== undefined && initialMax !== undefined) {
    registerHeightRangeIfMissing(initialMin, initialMax);
  }
  return getGlobalPropertyService().getProperty(PointCloudPropertyKeys.HEIGHT_RANGE, pointCloudId, PropertyType.PROJECT_USER) as ReactiveProperty<[number, number]>;
}

/**
 * Returns the intensity range property, registering it lazily if bounds are provided.
 * Pass `initialMin`/`initialMax` on first call; subsequent calls can omit them.
 */
export function getPointCloudIntensityRangeProperty(pointCloudId: string, initialMin?: number, initialMax?: number): ReactiveProperty<[number, number]> {
  if (initialMin !== undefined && initialMax !== undefined) {
    registerIntensityRangeIfMissing(initialMin, initialMax);
  }
  return getGlobalPropertyService().getProperty(PointCloudPropertyKeys.INTENSITY_RANGE, pointCloudId, PropertyType.PROJECT_USER) as ReactiveProperty<[number, number]>;
}

export function getPointCloudHiddenClassificationsProperty(pointCloudId: string): ReactiveProperty<number[]> {
  return getGlobalPropertyService().getProperty(PointCloudPropertyKeys.HIDDEN_CLASSIFICATIONS, pointCloudId, PropertyType.PROJECT_USER) as ReactiveProperty<number[]>;
}

export function getPointCloudHiddenSourcesProperty(pointCloudId: string): ReactiveProperty<number[]> {
  return getGlobalPropertyService().getProperty(PointCloudPropertyKeys.HIDDEN_SOURCES, pointCloudId, PropertyType.PROJECT_USER) as ReactiveProperty<number[]>;
}

/** Viewer-wide EDL toggle. Stored per-user, scoped to a fixed `'viewer'` objectIdentifier. */
export function getViewerEdlEnabledProperty(): ReactiveProperty<boolean> {
  return getGlobalPropertyService().getProperty(PointCloudPropertyKeys.VIEWER_EDL_ENABLED, VIEWER_OBJECT_ID, PropertyType.PROJECT_USER) as ReactiveProperty<boolean>;
}

// ============================================================================
// TYPE GUARDS / HELPERS
// ============================================================================

/** True if the resolution property holds a real user choice (not the `-1` "unset" sentinel). */
export function isExplicitResolution(value: PropertyValueData | undefined): value is number {
  return typeof value === 'number' && value !== -1;
}
